import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { extractFileFromDump } from '../lib/api';
import { formatBytes } from '../lib/utils';
import Convert from 'ansi-to-html';
import Prism from 'prismjs';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-yaml';

interface FileViewerProps {
  sourceUrl: string;
  runId: string;
  network: string;
  elClient: string;
  clClient: string;
  filePath: string;
  fileSize?: number;
  onClose?: () => void;
  initialFullWindow?: boolean;
  onFullWindowToggle?: (fullWindow: boolean) => void;
}

export function FileViewer({
  sourceUrl,
  runId,
  network,
  elClient,
  clClient,
  filePath,
  fileSize,
  onClose,
  initialFullWindow = false,
  onFullWindowToggle
}: FileViewerProps) {
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedSize, setLoadedSize] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [fullWindow, setFullWindow] = useState(initialFullWindow);
  const [highlightedLines, setHighlightedLines] = useState<number[]>([]);
  const [wrapLines, setWrapLines] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentRequestRef = useRef<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Update full window state when initialFullWindow changes
  useEffect(() => {
    setFullWindow(initialFullWindow);
  }, [initialFullWindow]);

  const toggleFullWindow = useCallback(() => {
    const newFullWindow = !fullWindow;
    setFullWindow(newFullWindow);
    if (onFullWindowToggle) {
      onFullWindowToggle(newFullWindow);
    }
  }, [fullWindow, onFullWindowToggle]);

  const toggleWrapLines = useCallback(() => {
    const newWrap = !wrapLines;
    setWrapLines(newWrap);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('wrap', newWrap.toString());
    setSearchParams(newParams);
  }, [wrapLines, searchParams, setSearchParams]);

  // Add ESC key listener for full window mode
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && fullWindow) {
        toggleFullWindow();
      }
    };

    if (fullWindow) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [fullWindow, toggleFullWindow]);

  // Handle line highlighting from URL parameters
  useEffect(() => {
    const lineParam = searchParams.get('lines');
    if (lineParam) {
      const lines = lineParam.split(',').map(line => {
        if (line.includes('-')) {
          const [start, end] = line.split('-').map(Number);
          return Array.from({ length: end - start + 1 }, (_, i) => start + i);
        }
        return [Number(line)];
      }).flat().filter(line => !isNaN(line) && line > 0);
      setHighlightedLines(lines);
    } else {
      // Clear highlighted lines if no lines parameter
      setHighlightedLines([]);
    }
  }, [searchParams, filePath]);

  // Scroll to highlighted lines when they change and content is loaded
  useEffect(() => {
    if (highlightedLines.length > 0 && content && !loading) {
      // Add a delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        const firstHighlightedLine = Math.min(...highlightedLines);
        // Try multiple selectors to find the line element
        let lineElement = contentRef.current?.querySelector(`[data-line-number="${firstHighlightedLine}"]`) as HTMLElement;

        if (!lineElement) {
          lineElement = document.querySelector(`[data-line-number="${firstHighlightedLine}"]`) as HTMLElement;
        }

        if (lineElement) {
          lineElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [highlightedLines, content, loading]);

  // Function to handle line clicks for permalinks
  const handleLineClick = (lineNumber: number, event: React.MouseEvent) => {
    event.preventDefault();
    const newParams = new URLSearchParams(searchParams);

    if (event.metaKey || event.ctrlKey) {
      // Multi-select with Ctrl/Cmd
      const currentLines = highlightedLines.includes(lineNumber)
        ? highlightedLines.filter(l => l !== lineNumber)
        : [...highlightedLines, lineNumber].sort((a, b) => a - b);

      if (currentLines.length === 0) {
        newParams.delete('lines');
      } else {
        newParams.set('lines', currentLines.join(','));
      }
      setHighlightedLines(currentLines);
    } else if (event.shiftKey && highlightedLines.length > 0) {
      // Range select with Shift
      const lastSelected = Math.max(...highlightedLines);
      const start = Math.min(lineNumber, lastSelected);
      const end = Math.max(lineNumber, lastSelected);
      const rangeLines = Array.from({ length: end - start + 1 }, (_, i) => start + i);

      newParams.set('lines', `${start}-${end}`);
      setHighlightedLines(rangeLines);
    } else {
      // Single select
      newParams.set('lines', lineNumber.toString());
      setHighlightedLines([lineNumber]);
    }

    setSearchParams(newParams);

    // Copy permalink to clipboard
    const url = `${window.location.origin}${window.location.pathname}#${window.location.hash.split('?')[0]}?${newParams.toString()}`;
    navigator.clipboard.writeText(url).catch(() => {
      // Fallback if clipboard API is not available - ignore silently
    });
  };

  const getFileType = (path: string): string => {
    const fileName = path.split('/').pop()?.toLowerCase() || '';

    // Check for specific filenames first
    if (fileName === 'jwtsecret') {
      return 'text';
    }

    const ext = path.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'log':
      case 'txt':
        return 'text';
      case 'json':
        return 'json';
      case 'yaml':
      case 'yml':
        return 'yaml';
      case 'toml':
        return 'toml';
      case 'xml':
        return 'xml';
      case 'sh':
      case 'bash':
        return 'shell';
      default:
        return 'unknown';
    }
  };

  const fileType = getFileType(filePath);
  const isViewable = ['text', 'json', 'yaml', 'toml', 'xml', 'shell'].includes(fileType);

  // Initialize wrap setting from URL, or default to wrap for plain-text/log files
  useEffect(() => {
    const wrapParam = searchParams.get('wrap');
    if (wrapParam === 'true') {
      setWrapLines(true);
    } else if (wrapParam === 'false') {
      setWrapLines(false);
    } else {
      setWrapLines(fileType === 'text');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, fileType]);

  // Reset state when file changes
  useEffect(() => {
    setContent(null);
    setError(null);
    setLoadedSize(null);
    setLoading(true);
    currentRequestRef.current = filePath; // Mark this as the current request
  }, [filePath]);

  // Auto-load viewable files
  useEffect(() => {
    if (isViewable) {
      loadFile();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filePath, isViewable]);

  const loadFile = async () => {
    const requestId = filePath; // Capture current filePath

    try {
      setLoading(true);
      setError(null);

      const blob = await extractFileFromDump(sourceUrl, runId, network, elClient, clClient, filePath);

      // Check if this request is still current
      if (currentRequestRef.current !== requestId) {
        return; // Request was superseded, ignore the result
      }

      setLoadedSize(blob.size);

      // Check if file is too large (limit to 5MB for text viewing)
      if (blob.size > 5 * 1024 * 1024) {
        if (currentRequestRef.current === requestId) {
          setError('File is too large to display. Please download it instead.');
        }
        return;
      }

      // Try to read as text
      const text = await blob.text();

      // Check again if this request is still current before setting content
      if (currentRequestRef.current === requestId) {
        setContent(text);
      }
    } catch (err) {
      // Only set error if this request is still current
      if (currentRequestRef.current === requestId) {
        setError(err instanceof Error ? err.message : 'Failed to load file');
      }
    } finally {
      // Only set loading to false if this request is still current
      if (currentRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  const downloadFile = async () => {
    try {
      setLoading(true);
      setError(null);

      const blob = await extractFileFromDump(sourceUrl, runId, network, elClient, clClient, filePath);

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath.split('/').pop() || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download file');
    } finally {
      setLoading(false);
    }
  };

  const copyContent = async () => {
    if (!content) return;

    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      // Failed to copy content - ignore silently
    }
  };

  const buildLineContents = (text: string, lines: string[]): { nodes: React.ReactNode[]; wrapperClass: string } => {
    if (fileType === 'json') {
      try {
        const highlightedHtml = Prism.highlight(text, Prism.languages.json, 'json');
        return {
          nodes: highlightedHtml.split('\n').map((htmlLine, index) => (
            <span key={index} dangerouslySetInnerHTML={{ __html: htmlLine }} />
          )),
          wrapperClass: 'syntax-json'
        };
      } catch (e) {
        // Fallback to plain
      }
    }

    if (fileType === 'yaml') {
      try {
        const highlightedHtml = Prism.highlight(text, Prism.languages.yaml, 'yaml');
        return {
          nodes: highlightedHtml.split('\n').map((htmlLine, index) => (
            <span key={index} dangerouslySetInnerHTML={{ __html: htmlLine }} />
          )),
          wrapperClass: 'syntax-yaml'
        };
      } catch (e) {
        // Fallback to plain
      }
    }

    // Route all other text through ansi-to-html: it converts ANSI SGR
    // sequences to styled spans and safely XML-escapes lines that have none,
    // so mixed files (plain header + ANSI body) render correctly.
    const convert = new Convert({
      newline: false,
      escapeXML: true,
      stream: false,
    });
    return {
      nodes: lines.map((line, index) => (
        <span key={index} dangerouslySetInnerHTML={{ __html: convert.toHtml(line) }} />
      )),
      wrapperClass: ''
    };
  };

  const renderContentWithLineNumbers = (text: string, isFullWindow = false) => {
    const lines = text.split('\n');
    const { nodes: lineContents, wrapperClass } = buildLineContents(text, lines);

    const heightClass = isFullWindow
      ? "h-full"
      : "max-h-[calc(100vh-24rem)]";

    const contentStyle: React.CSSProperties = wrapLines
      ? { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', textShadow: 'none' }
      : { whiteSpace: 'pre', textShadow: 'none' };

    return (
      <div
        ref={contentRef}
        className={`text-xs font-mono bg-muted overflow-hidden ${heightClass} overflow-y-auto`}
        style={{ overflowX: wrapLines ? 'hidden' : 'auto' }}
      >
        <style dangerouslySetInnerHTML={{
          __html: `
            .syntax-json .token.property { color: #0066cc; }
            .syntax-json .token.string { color: #22863a; }
            .syntax-json .token.number { color: #005cc5; }
            .syntax-json .token.boolean { color: #d73a49; }
            .syntax-json .token.null { color: #6f42c1; }
            .syntax-json .token.punctuation { color: #586069; }
            .syntax-yaml .token.key { color: #0066cc; }
            .syntax-yaml .token.string { color: #22863a; }
            .syntax-yaml .token.number { color: #005cc5; }
            .syntax-yaml .token.boolean { color: #d73a49; }
            .syntax-yaml .token.null { color: #6f42c1; }
            .syntax-yaml .token.punctuation { color: #586069; }
            .syntax-yaml .token.comment { color: #6a737d; font-style: italic; }
          `
        }} />
        <div className={wrapLines ? "py-4" : "py-4 min-w-fit"}>
          {lines.map((_, index) => {
            const lineNumber = index + 1;
            const isHighlighted = highlightedLines.includes(lineNumber);

            return (
              <div
                key={index}
                className={`flex ${isHighlighted ? 'bg-blue-500/10' : ''}`}
              >
                <div
                  data-line-number={lineNumber}
                  className={`shrink-0 sticky left-0 z-10 cursor-pointer select-none text-right border-r border-border ${
                    isHighlighted
                      ? 'bg-blue-500/20 text-blue-600 font-semibold'
                      : 'bg-muted-foreground/10 hover:bg-muted-foreground/20 text-muted-foreground'
                  }`}
                  onClick={(e) => handleLineClick(lineNumber, e)}
                  title="Click to select line, Ctrl+click to multi-select, Shift+click for range"
                  style={{ minWidth: '3rem', padding: '0 0.5rem', lineHeight: '1.25rem' }}
                >
                  {lineNumber}
                </div>
                <div
                  className={`flex-1 min-w-0 px-4 ${wrapperClass}`}
                  style={{
                    minHeight: '1.25rem',
                    lineHeight: '1.25rem',
                    ...contentStyle,
                  }}
                >
                  {lineContents[index]}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (fullWindow && content) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        <div className="bg-background border-b p-4 flex-shrink-0">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <span>📄</span>
              <span className="font-mono text-sm">{filePath}</span>
              {loadedSize && <Badge variant="outline">{formatBytes(loadedSize)}</Badge>}
            </div>
            <div className="flex gap-2">
              <Button onClick={toggleWrapLines} size="sm" variant="outline">
                {wrapLines ? 'Unwrap' : 'Wrap'}
              </Button>
              <Button
                onClick={copyContent}
                size="sm"
                variant="outline"
                className={copied ? 'text-green-600' : ''}
              >
                {copied ? '✓ Copied' : 'Copy'}
              </Button>
              <Button onClick={downloadFile} size="sm" variant="outline">
                Download
              </Button>
              <Button onClick={toggleFullWindow} size="sm" variant="outline">
                Exit Full Window
              </Button>
            </div>
          </div>
        </div>
        <div className="flex-1 p-4 min-h-0">
          {renderContentWithLineNumbers(content, true)}
        </div>
      </div>
    );
  }

  return (
    <Card className="border-0">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span>📄</span>
            <span className="font-mono text-sm">{filePath}</span>
            {fileSize && <Badge variant="outline">{formatBytes(fileSize)}</Badge>}
          </div>
          <div className="flex gap-2">
            {loading && isViewable && (
              <Badge variant="outline" className="text-xs">
                Loading...
              </Badge>
            )}
            {content && (
              <Button onClick={toggleWrapLines} size="sm" variant="outline">
                {wrapLines ? 'Unwrap' : 'Wrap'}
              </Button>
            )}
            {content && (
              <Button
                onClick={copyContent}
                size="sm"
                variant="outline"
                className={copied ? 'text-green-600' : ''}
              >
                {copied ? '✓ Copied' : 'Copy'}
              </Button>
            )}
            <Button onClick={downloadFile} disabled={loading} size="sm" variant="outline">
              Download
            </Button>
            {content && (
              <Button onClick={toggleFullWindow} size="sm" variant="outline">
                Full Window
              </Button>
            )}
            {onClose && (
              <Button onClick={onClose} size="sm" variant="ghost">
                ×
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      {(content || error || (loading && isViewable)) && (
        <CardContent>
          {error ? (
            <div className="text-red-600 dark:text-red-400 text-sm">{error}</div>
          ) : content ? (
            <div>
              {loadedSize && (
                <div className="text-xs text-muted-foreground mb-2">
                  Loaded: {formatBytes(loadedSize)}
                </div>
              )}
              {renderContentWithLineNumbers(content, false)}
            </div>
          ) : loading && isViewable ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span className="ml-2 text-sm text-muted-foreground">Loading file content...</span>
            </div>
          ) : null}
        </CardContent>
      )}
    </Card>
  );
}

export default FileViewer;
